import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { membersAPI, eventsAPI, unwrapList } from '../../services/api';
import { LoadingPage } from '../../components/Loading';
import { useToast } from '../../context/ToastContext';
import './Pos.css';

const statusLabel = {
  confirmed: '已確認',
  pending: '待確認',
  cancelled: '已取消',
  present: '已出席',
  absent: '缺席',
};

const MemberCard = () => {
  const { id } = useParams();
  const { success, error } = useToast();
  const [member, setMember] = useState(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState('');
  const [registerMessage, setRegisterMessage] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await membersAPI.getMember(id);
        if (!cancelled) {
          setMember(response.data);
          setNotes(response.data.notes || '');
        }
      } catch (err) {
        if (!cancelled) {
          error(err.response?.data?.error || '找不到會員');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    eventsAPI.adminList()
      .then((response) => {
        const rows = unwrapList(response.data);
        setEvents(rows);
        if (rows[0]) setEventId(String(rows[0].id));
      })
      .catch(() => setEvents([]));
  }, []);

  const register = async () => {
    if (!eventId) return;
    setRegisterMessage('');
    try {
      const response = await eventsAPI.registerMember(eventId, id);
      setRegisterMessage(response.data.message);
      const refreshed = await membersAPI.getMember(id);
      setMember(refreshed.data);
    } catch (err) {
      setRegisterMessage(err.response?.data?.message || '報名失敗');
    }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      const response = await membersAPI.patch(id, { notes });
      setMember(response.data);
      setNotes(response.data.notes || '');
      success('備註已儲存');
    } catch (err) {
      error('備註儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingPage message="載入會員卡..." />;
  if (!member) return <div className="pos-error">找不到會員</div>;

  const active = member.is_active;

  return (
    <article className="pos-card">
      <p><Link to="/admin/scan">返回掃描</Link></p>
      <h1>{member.chinese_name}</h1>
      <div className={`pos-status ${active ? 'green' : 'red'}`}>
        {active ? '有效' : '已停用'}
      </div>
      <div className="pos-meta">
        <div>會員編號：{member.member_number}</div>
        <div>英文姓名：{member.english_name || '—'}</div>
        <div>電話：{member.phone_number || '—'}</div>
        <div>電郵：{member.email || '—'}</div>
        <div>地區：{member.district || '—'}</div>
        <div>驗證：{member.is_verified ? '已驗證' : '未驗證'}</div>
      </div>

      <section className="pos-section">
        <h2>活動報名</h2>
        {events.length === 0 ? (
          <p>暫無活動</p>
        ) : (
          <>
            <select className="pos-station-select" value={eventId} onChange={(e) => setEventId(e.target.value)}>
              {events.map((event) => (
                <option key={event.id} value={event.id}>{event.title}</option>
              ))}
            </select>
            <button type="button" className="pos-btn" onClick={register}>報名此活動</button>
            {registerMessage && <p className="pos-hint">{registerMessage}</p>}
          </>
        )}
      </section>

      <section className="pos-section">
        <h2>活動記錄</h2>
        {member.registrations?.length || member.attendances?.length ? (
          <ul className="pos-list">
            {(member.registrations || []).map((row) => (
              <li key={`reg-${row.id}`}>
                報名 · {row.event_title} · {statusLabel[row.status] || row.status}
              </li>
            ))}
            {(member.attendances || []).map((row) => (
              <li key={`att-${row.id}`}>
                簽到 · {row.event_title} · {statusLabel[row.status] || row.status}
              </li>
            ))}
          </ul>
        ) : (
          <p>暫無活動記錄</p>
        )}
      </section>

      <section className="pos-section">
        <h2>禮品記錄</h2>
        {member.gifts?.length ? (
          <ul className="pos-list">
            {member.gifts.map((gift) => (
              <li key={gift.id}>{gift.batch_name} · {gift.redeemed_at}</li>
            ))}
          </ul>
        ) : (
          <p>暫無禮品記錄</p>
        )}
      </section>

      <section className="pos-section">
        <h2>職員備註</h2>
        <textarea
          className="pos-notes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
        <div className="pos-actions">
          <button type="button" className="pos-btn" onClick={saveNotes} disabled={saving}>
            {saving ? '儲存中...' : '儲存備註'}
          </button>
        </div>
      </section>
    </article>
  );
};

export default MemberCard;
